import { getApiBaseUrl } from './appSettings';
import { getActivityContextHeaders } from './activityContextHeaders';

let activeApiRequestCount = 0;
const SILENT_LOADER_ENDPOINTS = new Set(['TrackActivity', 'VerifyUser']);

const getEndpointName = (endpoint) => String(endpoint || '').split(/[/?]/)[0];

const shouldShowGlobalLoader = (endpoint) => !SILENT_LOADER_ENDPOINTS.has(getEndpointName(endpoint));

const emitApiActivity = () => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(
    new CustomEvent('ainsoft:api-activity', {
      detail: { active: activeApiRequestCount > 0, count: activeApiRequestCount },
    })
  );
};

const beginApiActivity = (endpoint) => {
  if (!shouldShowGlobalLoader(endpoint)) return false;
  activeApiRequestCount += 1;
  emitApiActivity();
  return true;
};

const endApiActivity = (isTracked) => {
  if (!isTracked) return;
  activeApiRequestCount = Math.max(0, activeApiRequestCount - 1);
  emitApiActivity();
};

// 🔹 Reusable fetch helper
const apiRequest = async (endpoint, method = 'GET', data = null, requiresAuth = false, isFormData = false, throwOnHttpError = false) => {
  const activityTracked = beginApiActivity(endpoint);
  const headers = { Accept: 'application/json' };

  if (requiresAuth) {
    const token = localStorage.getItem('authToken');
    if (token) headers.Authorization = `Basic ${token}`;
  }

  const ctx = getActivityContextHeaders(endpoint);
  Object.assign(headers, ctx);

  let url = `${getApiBaseUrl()}${endpoint}`;
  const options = { method, headers };

  // 🔸 If it's GET, append query params
  if (data && method.toUpperCase() === 'GET') { 
    url += `/${data}`;
  }

  // 🔸 For POST/PUT/PATCH etc., send JSON body or FormData
  if (method.toUpperCase() !== 'GET') {
    if (data !== null && data !== undefined) {
      if (isFormData) {
        // Don't set Content-Type for FormData, browser will set it with boundary
        options.body = data;
      } else {
        headers['Content-Type'] = 'application/json';
        options.body = JSON.stringify(data);
      }
    }
  }

  try {
    const response = await fetch(url, options);
    const text = await response.text();
    const trimmed = text.trim();
    if (throwOnHttpError && !response.ok) {
      const msg = trimmed ? trimmed.slice(0, 400) : `HTTP ${response.status}`;
      throw new Error(msg);
    }
    if (!trimmed) return null;

    try {
      return JSON.parse(text);
    } catch {
      const t = trimmed;
      if (['true', 'false', 'Success', 'Failed'].includes(t)) return t;
      return text;
    }
  } catch (error) {
    console.error(`API Error (${endpoint}):`, error);
    throw error;
  } finally {
    endApiActivity(activityTracked);
  }
};

// 🔹 Main Service
const MainService = {
  // Auth
  Login: (data) => apiRequest('Login', 'POST', data),
  ChangePassword: (data) => apiRequest('ChangePassword', 'POST', data, true),
  /** GET with Authorization; returns true if session valid and user not blocked, else false */
  VerifyUser: () => apiRequest('VerifyUser', 'GET', null, true),
  GetCompanyProfile: () => apiRequest('GetCompanyProfile', 'GET', null),
  SaveCompanyProfile: (data) => apiRequest('SaveCompanyProfile', 'POST', data, true),
  FinYearInDropDown: () => apiRequest('FinYearInDropDown', 'GET', null, true),
  accountInAutoComplete: (data) => apiRequest('accountInAutoComplete', 'POST', data || {}, true),
  accountInAutoCompleteLevel3: (data) => apiRequest('accountInAutoCompleteLevel3', 'POST', data || {}, true),
  LogOutAll: () => apiRequest('LogOutAll', 'GET', null, true),

  // Emirates
  SaveEmirate: (data) => apiRequest('SaveEmirate', 'POST', data, true),
  EmiratesInDropDown: () => apiRequest('EmiratesInDropDown', 'GET', null, true),
  DeleteEmirate: (data) => apiRequest('DeleteEmirate', 'GET', data, true),
  AreasInDropDown: () => apiRequest('AreasInDropDown', 'GET', null, true),
  DesignationsInDropDown: () => apiRequest('DesignationsInDropDown', 'GET', null, true),
  ManagersInDropDown: () => apiRequest('ManagersInDropDown', 'GET', null, true),
  VehiclesInDropDown: () => apiRequest('VehiclesInDropDown', 'GET', null, true),
  DepartmentsInDropDown: () => apiRequest('DepartmentsInDropDown', 'GET', null, true),

  // Locations
  SaveLocation: (data) => apiRequest('SaveLocation', 'POST', data, true),
  UploadBulkLocations: (rows) => apiRequest('UploadBulkLocations', 'POST', rows, true),
  LocationsInGrid: (data) => apiRequest('LocationsInGrid', 'POST', data || {}, true),
  /** New React locations list — no Kendo. { Skip, Take, Keyword, SortField, SortDir, Filters } */
  LocationsList: (data) => apiRequest('LocationsList', 'POST', data || {}, true),
  DeleteLocation: (data) => apiRequest('DeleteLocation', 'GET', data, true),
  GetLocationsByEid: (emirateId) => apiRequest('GetLocationsByEid', 'GET', emirateId, true),
  LocationsAutoCompleteByEmirate: (data) => apiRequest('LocationsAutoCompleteByEmirate', 'POST', data, true),
  UploadLocations: (formData) => apiRequest('UploadLocations', 'POST', formData, true, true),
  BulkUpdateLocations: (data) => apiRequest('BulkUpdateLocations', 'POST', data, true),

  // Status, Reason, Type, etc.
  SaveStatus: (data) => apiRequest('SaveStatus', 'POST', data, true),
  SaveReason: (data) => apiRequest('SaveReason', 'POST', data, true),
  ReasonsInGrid: (data) => apiRequest('ReasonsInGrid', 'POST', data || {}, true),
  DeleteReason: (data) => apiRequest('DeleteReason', 'GET', data, true),
  SaveType: (data) => apiRequest('SaveType', 'POST', data, true),
  TypesInGrid: (data) => apiRequest('TypesInGrid', 'POST', data || {}, true),
  DeleteType: (data) => apiRequest('DeleteType', 'GET', data, true),
  StatusInShipping: (data) => apiRequest('StatusInShipping', 'GET', data, false),
  StatusInGrid: (data) => apiRequest('statusInGrid', 'POST', data || {}, true),
  StatusInDropDown: () => apiRequest('StatusInDropDown', 'GET', null, true),
  EnableDriverStatus: (id) => apiRequest(`EnableDriverStatus/${id}`, 'POST', null, true),
  UpdateOrderDeliveryStatus: (data) => apiRequest('UpdateOrderDeliveryStatus', 'POST', data, true),

  // Expenses
  SaveExpenses: (data) => apiRequest('SaveExpenses', 'POST', data, true),
  ExpensesInGrid: (data) => apiRequest('ExpensesInGrid', 'POST', data || {}, true),
  ExpensesInDropDown: () => apiRequest('ExpensesInDropDown', 'GET', null, true),
  DeleteExpense: (data) => apiRequest('DeleteExpense', 'GET', data, true),
  DriversExpensesInDropDown: () => apiRequest('DriversExpensesInDropDown', 'GET', null, true),
  getDriverExpenseInShipping: (driverId) => apiRequest('getDriverExpenseInShipping', 'GET', driverId, true),
  saveDriverExpense: (data) => apiRequest('saveDriverExpense', 'POST', data, true),
  deleteDriverExpense: (id) => apiRequest('deleteDriverExpense', 'GET', id, true),
  DriverExpenseInView: (data) => apiRequest('DriverExpenseInView', 'POST', data || {}, true),
  CompanyExpenseInGrid: (data) => apiRequest('CompanyExpenseInGrid', 'POST', data || {}, true),
  SaveCompanyExpense: (data) => apiRequest('SaveCompanyExpense', 'POST', data, true),
  DeleteCompanyExpense: (id) => apiRequest('DeleteCompanyExpense', 'GET', id, true),
  getShipmentExpenses: (shipmentId) => apiRequest('getShipmentExpenses', 'GET', shipmentId, true),

  // Areas
  SaveArea: (data) => apiRequest('SaveArea', 'POST', data, true),
  DeleteArea: (data) => apiRequest('DeleteArea', 'GET', data, true),

  // Drivers / Vehicles / Designations
  DriversInDropDown: () => apiRequest('DriversInDropDown', 'GET', null, true),
  DriversInDriverAllocation: () => apiRequest('DriversInDriverAllocation', 'GET', null, true),
  SaveVehicle: (data) => apiRequest('SaveVehicle', 'POST', data, true),
  VehicleById: (id) => apiRequest('VehicleById', 'GET', id, true),
  VehiclesInGrid: (data) => apiRequest('VehiclesInGrid', 'POST', data || {}, true),
  DeleteVehicle: (data) => apiRequest('DeleteVehicle', 'GET', data, true),

  // Vehicle management (assignments, fuel, permits, inspections, odometer, accidents, expenses, service history)
  VehicleAssignmentsInGrid: (data) =>
    apiRequest('VehicleAssignmentsInGrid', 'POST', data || {}, true),
  SaveVehicleAssignment: (data) => apiRequest('SaveVehicleAssignment', 'POST', data, true),
  DeleteVehicleAssignment: (id) => apiRequest('DeleteVehicleAssignment', 'GET', id, true),
  VehicleAssignmentById: (id) => apiRequest('VehicleAssignmentById', 'GET', id, true),

  VehicleFuelLogsInGrid: (data) => apiRequest('VehicleFuelLogsInGrid', 'POST', data || {}, true),
  SaveVehicleFuelLog: (data) => apiRequest('SaveVehicleFuelLog', 'POST', data, true),
  DeleteVehicleFuelLog: (id) => apiRequest('DeleteVehicleFuelLog', 'GET', id, true),

  VehiclePermitsInGrid: (data) => apiRequest('VehiclePermitsInGrid', 'POST', data || {}, true),
  SaveVehiclePermit: (data) => apiRequest('SaveVehiclePermit', 'POST', data, true),
  DeleteVehiclePermit: (id) => apiRequest('DeleteVehiclePermit', 'GET', id, true),

  VehicleInspectionsInGrid: (data) => apiRequest('VehicleInspectionsInGrid', 'POST', data || {}, true),
  SaveVehicleInspection: (data) => apiRequest('SaveVehicleInspection', 'POST', data, true),
  DeleteVehicleInspection: (id) => apiRequest('DeleteVehicleInspection', 'GET', id, true),

  VehicleOdometerLogsInGrid: (data) => apiRequest('VehicleOdometerLogsInGrid', 'POST', data || {}, true),
  SaveVehicleOdometerLog: (data) => apiRequest('SaveVehicleOdometerLog', 'POST', data, true),
  DeleteVehicleOdometerLog: (id) => apiRequest('DeleteVehicleOdometerLog', 'GET', id, true),

  VehicleAccidentsInGrid: (data) => apiRequest('VehicleAccidentsInGrid', 'POST', data || {}, true),
  SaveVehicleAccident: (data) => apiRequest('SaveVehicleAccident', 'POST', data, true),
  DeleteVehicleAccident: (id) => apiRequest('DeleteVehicleAccident', 'GET', id, true),

  VehicleExpensesInGrid: (data) => apiRequest('VehicleExpensesInGrid', 'POST', data || {}, true),
  SaveVehicleExpense: (data) => apiRequest('SaveVehicleExpense', 'POST', data, true),
  DeleteVehicleExpense: (id) => apiRequest('DeleteVehicleExpense', 'GET', id, true),

  VehicleServiceHistoryInGrid: (data) =>
    apiRequest('VehicleServiceHistoryInGrid', 'POST', data || {}, true),
  SaveVehicleServiceHistory: (data) => apiRequest('SaveVehicleServiceHistory', 'POST', data, true),
  DeleteVehicleServiceHistory: (id) => apiRequest('DeleteVehicleServiceHistory', 'GET', id, true),
  VehicleServiceHistoryById: (id) => apiRequest('VehicleServiceHistoryById', 'GET', id, true),

  VehicleDocumentsInGrid: (data) => apiRequest('VehicleDocumentsInGrid', 'POST', data || {}, true),
  SaveVehicleDocument: (data) => apiRequest('SaveVehicleDocument', 'POST', data, true),
  DeleteVehicleDocument: (id) => apiRequest('DeleteVehicleDocument', 'GET', id, true),
  VehicleDocumentById: (id) => apiRequest('VehicleDocumentById', 'GET', id, true),

  VehicleInsurancesInGrid: (data) => apiRequest('VehicleInsurancesInGrid', 'POST', data || {}, true),
  SaveVehicleInsurance: (data) => apiRequest('SaveVehicleInsurance', 'POST', data, true),
  DeleteVehicleInsurance: (id) => apiRequest('DeleteVehicleInsurance', 'GET', id, true),
  VehicleInsuranceById: (id) => apiRequest('VehicleInsuranceById', 'GET', id, true),

  VehicleFineRecordsInGrid: (data) => apiRequest('VehicleFineRecordsInGrid', 'POST', data || {}, true),
  SaveVehicleFineRecord: (data) => apiRequest('SaveVehicleFineRecord', 'POST', data, true),
  DeleteVehicleFineRecord: (id) => apiRequest('DeleteVehicleFineRecord', 'GET', id, true),
  VehicleFineRecordById: (id) => apiRequest('VehicleFineRecordById', 'GET', id, true),

  SaveDesignation: (data) => apiRequest('SaveDesignation', 'POST', data, true),
  DesignationsInGrid: (data) => apiRequest('DesignationsInGrid', 'POST', data || {}, true),
  DeleteDesignation: (data) => apiRequest('DeleteDesignation', 'GET', data, true),



  // Tracking
  TrackOrdersByVoucherNo: (data) => apiRequest('TrackOrdersByVoucherNo', 'POST', data, true),
  TrackOrdersByBillNosPortal: (data, token) => {
    const activityTracked = beginApiActivity('TrackOrdersByBillNosPortal');
    const headers = { Accept: 'application/json', 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Basic ${token}`;
    return fetch(`${getApiBaseUrl()}TrackOrdersByBillNosPortal`, {
      method: 'POST',
      headers,
      body: JSON.stringify(data)
    }).then(res => res.json()).finally(() => endApiActivity(activityTracked));
  },
  TrackOrdersByCustomerInPortal: (data, token) => {
    const activityTracked = beginApiActivity('TrackOrdersByCustomerInPortal');
    const headers = { Accept: 'application/json', 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Basic ${token}`;
    return fetch(`${getApiBaseUrl()}TrackOrdersByCustomerInPortal`, {
      method: 'POST',
      headers,
      body: JSON.stringify(data)
    }).then(res => res.json()).finally(() => endApiActivity(activityTracked));
  },
  OrderStatusByOrderId: (orderId) => apiRequest('OrderStatusByOrderId', 'GET', orderId, true),
  OrderHistoryByOrderId: (orderId) => apiRequest('OrderHistoryByOrderId', 'GET', orderId, true),
  GetPickupNoteDetailsByOrderId: (orderId) => apiRequest('GetPickupNoteDetailsByOrderId', 'GET', orderId, true),
  GetDeliveryNoteDetailsByOrderId: (orderId) => apiRequest('GetDeliveryNoteDetailsByOrderId', 'GET', orderId, true),
  OrderById: (data) => apiRequest('OrderById', 'GET', data, true),
  PrintOrder: (data) => apiRequest('PrintOrder', 'GET', data, true),
  getShipmentByOrderId: (data) => apiRequest('getShipmentByOrderId', 'GET', data, true),
  getClientPaymentByOrderId: (data) => apiRequest('getClientPaymentByOrderId', 'GET', data, true),
  getClientReturnByOrderId: (data) => apiRequest('getClientReturnByOrderId', 'GET', data, true),
  OrderAttachments: (data) => apiRequest('OrderAttachments', 'GET', data, true),
  SaveOrderAttachment: (formData) => apiRequest('SaveOrderAttachment', 'POST', formData, true, true),
  UpdateOrderRemarks: (data) => apiRequest('UpdateOrderRemarks', 'POST', data, true),
  EmployeesInList: (data) => apiRequest('EmployeesInList', 'POST', data, true),
  /** React accounts list — no Kendo. { Skip, Take, Keyword, SortField, SortDir, Filters } */
  EmployeesList: (data) => apiRequest('EmployeesList', 'POST', data || {}, true),
  EmployeeById: (id) => apiRequest('EmployeeById', 'GET', id, true),
  SaveEmployee: (data) => apiRequest('SaveEmployee', 'POST', data, true),
  EmployeesInUserDropDown: () => apiRequest('EmployeesInUserDropDown', 'GET', null, true),
  EmployeesInUserDropDownV2: (data) => apiRequest('EmployeesInUserDropDownV2', 'POST', data || {}, true),

  // Clients
  ClientsInList: (data) => apiRequest('ClientsInList', 'POST', data, true),
  /** React accounts list — no Kendo. { Skip, Take, Keyword, SortField, SortDir, Filters } */
  ClientsList: (data) => apiRequest('ClientsList', 'POST', data || {}, true),
  /** Lightweight client list for dropdowns / reports (typically keyword + paging). */
  ClientsInDropdownList: (data) => apiRequest('ClientsInDropdownList', 'POST', data || {}, true),
  ClientsInUserList: (data) => apiRequest('ClientsInUserList', 'POST', data || {}, true),
  ClientsInUserListV2: (data) => apiRequest('ClientsInUserListV2', 'POST', data || {}, true),
  ClientById: (id) => apiRequest('ClientById', 'GET', id, true),
  InActiveClientsInGridV2: (data) => apiRequest('InActiveClientsInGridV2', 'POST', data || {}, true),
  SaveClient: (data) => apiRequest('SaveClient', 'POST', data, true),
  DeleteClient: (id) => apiRequest('DeleteClient', 'GET', id, true),
  GetNextClientNo: () => apiRequest('GetNextClientNo', 'GET', null, true),
  
  // Customers
  CustomersInList: (data) => apiRequest('CustomersInList', 'POST', data, true),
  /** React accounts list — no Kendo. { Skip, Take, Keyword, SortField, SortDir, Filters } */
  CustomersList: (data) => apiRequest('CustomersList', 'POST', data || {}, true),
  CustomerById: (id) => apiRequest('CustomerById', 'GET', id, true),
  SaveCustomer: (data) => apiRequest('SaveCustomer', 'POST', data, true),
  UpdateCustomerAddress: (data) => apiRequest('UpdateCustomerAddress', 'POST', data, true),
  
  // Agents
  AgentsInList: (data) => apiRequest('AgentsInList', 'POST', data, true),
  /** React accounts list — no Kendo. { Skip, Take, Keyword, SortField, SortDir, Filters } */
  AgentsList: (data) => apiRequest('AgentsList', 'POST', data || {}, true),
  AgentById: (id) => apiRequest('AgentById', 'GET', id, true),
  SaveAgent: (data) => apiRequest('SaveAgent', 'POST', data, true),

  // Users
  UsersInList: (data) => apiRequest('UsersInList', 'POST', data || {}, true),
  UsersInListV2: (data) => apiRequest('UsersInListV2', 'POST', data || {}, true),
  /** React users list — no Kendo. { Skip, Take, Keyword, SortField, SortDir, Filters, Type, Status } */
  UsersList: (data) => apiRequest('UsersList', 'POST', data || {}, true),
  SaveUser: (data) => apiRequest('SaveUser', 'POST', data, true),
  SaveUserV2: (data) => apiRequest('SaveUserV2', 'POST', data, true),
  BlockUser: (id) => apiRequest('BlockUser', 'GET', id, true),
  BlockUserV2: (id) => apiRequest(`BlockUserV2/${id}`, 'POST', null, true),
  EmployeeUsersInDropDown: () => apiRequest('EmployeeUsersInDropDown', 'GET', null, true),
  GetEmployeePageRoleDetails: (id) => apiRequest('GetEmployeePageRoleDetails', 'GET', id, true),
  AddUserPageRole: (data) => apiRequest('AddUserPageRole', 'POST', data || [], true),
  CheckUserPageAccess: (data) => apiRequest('CheckUserPageAccess', 'POST', data || {}, true),
  
  // Employees Dropdown (for ResponsibleBy)
  EmployeesInDropDown: () => apiRequest('EmployeesInDropDown', 'GET', null, true),
  EmployeeTimeline: (data) => apiRequest('EmployeeTimeline', 'POST', data, true),

  // Commissions
  CommissionList: (data) => apiRequest('CommissionList', 'POST', data, true),
  SaveCommission: (data) => apiRequest('SaveCommission', 'POST', data, true),
  DeleteCommission: (id) => apiRequest('DeleteCommission', 'GET', id, true),

  // Voucher Book
  SaveVoucherBook: (data) => apiRequest('SaveVoucherBook', 'POST', data, true),
  UpdateVoucherBook: (data) => apiRequest('UpdateVoucherBook', 'POST', data, true),
  VoucherBookInList: (data) => apiRequest('VoucherBookInList', 'POST', data || {}, true),
  DeleteVoucherBook: (id) => apiRequest('DeleteVoucherBook', 'GET', id, true),
  DeleteBulkVoucherBook: (data) => apiRequest('DeleteBulkVoucherBook', 'POST', data, true),
  PrintVoucherBook: (data) => apiRequest('PrintVoucherBook', 'POST', data, true),

  // Client Area Charges
  SaveClientAreaCharges: (data) => apiRequest('SaveClientAreaCharges', 'POST', data, true),
  ClientAreachargesInList: (data) => apiRequest('ClientAreachargesInList', 'POST', data || {}, true),
  ClientAreaChargesPriceGrid: (data) => apiRequest('ClientAreaChargesPriceGrid', 'POST', data || {}, true),
  GetClientPriceChart: (clientId) => apiRequest('GetClientPriceChart', 'GET', clientId, true),
  GetClientPriceCard: (clientId) => apiRequest('GetClientPriceCard', 'GET', clientId, true),
  DeleteClientAreaCharge: (id) => apiRequest('DeleteClientAreaCharge', 'GET', id, true),

  // SMS Configuration
  MessagesInGrid: (data) => apiRequest('MessagesInGrid', 'POST', data, true),
  UpdateMessageList: (data) => apiRequest('UpdateMessageList', 'POST', data, true),

  // WhatsApp Configuration
  WhatsAppMessagesInGrid: (data) => apiRequest('WhatsAppMessagesInGrid', 'POST', data || {}, true),
  SaveWhatsAppMessage: (data) => apiRequest('SaveWhatsAppMessage', 'POST', data, true),
  DeleteWhatsAppMessage: (id) => apiRequest('DeleteWhatsAppMessage', 'GET', id, true),

  // Document Number
  GetTraceNumbers: (data) => apiRequest('GetTraceNumbers', 'POST', data || {}, true),
  GetTraceNumbersList: (data) => apiRequest('GetTraceNumbersList', 'POST', data || {}, true),
  UpdateTraceNumber: (data) => apiRequest('UpdateTraceNumber', 'POST', data, true),

  // Order Upload
  ClientsInOrderAutoComplete: (data) => apiRequest('ClientsInOrderAutoComplete', 'POST', data, true),
  GetClientAddress: (clientId) => apiRequest('GetClientAddress', 'GET', clientId, true),
  GetDeliveryCharge: (data) => apiRequest('GetDeliveryCharge', 'POST', data, true),
  SaveOrderList: (data) => apiRequest('SaveOrderList', 'POST', data, true),
  PrintOrderList: (data) => apiRequest('PrintOrderList', 'POST', data, true),
  LocationByEmirateId: (emirateId) => apiRequest('LocationByEmirateId', 'GET', emirateId, true),
  getLocationByNameEmiId: (data) => apiRequest('getLocationByNameEmiId', 'POST', data, true),
  
  // Order Management
  GetOrderList: (data) => apiRequest('GetOrderList', 'POST', data || {}, true),
  GetOrderListList: (data) => apiRequest('GetOrderListList', 'POST', data || {}, true),
  GetOrderCalendarMonth: (data) => apiRequest('GetOrderCalendarMonth', 'POST', data || {}, true),
  OrderReportV2: (data) => apiRequest('OrderReportV2', 'POST', data || {}, true),
  ClientOrderReportV2: (data) => apiRequest('ClientOrderReportV2', 'POST', data || {}, true),
  CustomerOrderReportV2: (data) => apiRequest('CustomerOrderReportV2', 'POST', data || {}, true),
  EmployeeOrderReportV2: (data) => apiRequest('EmployeeOrderReportV2', 'POST', data || {}, true),
  AgentOrderReportV2: (data) => apiRequest('AgentOrderReportV2', 'POST', data || {}, true),
  AdvanceOrderReportV2: (data) => apiRequest('AdvanceOrderReportV2', 'POST', data || {}, true),
  PendingOrderReportV2: (data) => apiRequest('PendingOrderReportV2', 'POST', data || {}, true),
  DriverExpenseReportV2: (data) => apiRequest('DriverExpenseReportV2', 'POST', data || {}, true),
  ShipmentExpenseReportV2: (data) => apiRequest('ShipmentExpenseReportV2', 'POST', data || {}, true),
  VatReportV2: (data) => apiRequest('VatReportV2', 'POST', data || {}, true),
  CompanyExpenseReportV2: (data) => apiRequest('CompanyExpenseReportV2', 'POST', data || {}, true),
  GetIncomeExpenseReportV2: (data) => apiRequest('GetIncomeExpenseReportV2', 'POST', data || {}, true),
  ClientPaymentReportV2: (data) => apiRequest('ClientPaymentReportV2', 'POST', data || {}, true),
  ClientReturnReportV2: (data) => apiRequest('ClientReturnReportV2', 'POST', data || {}, true),
  ShippingsInReportV2: (data) => apiRequest('ShippingsInReportV2', 'POST', data || {}, true),
  PrintIndClientPayment: (id) => apiRequest('PrintIndClientPayment', 'GET', id, true),
  PrintIndClientReturn: (id) => apiRequest('PrintIndClientReturn', 'GET', id, true),
  DriverCommissionReportV2: (data) => apiRequest('DriverCommissionReportV2', 'POST', data || {}, true),
  DriversInGrid: (data) => apiRequest('DriversInGrid', 'POST', data || {}, true),
  DailyDeliveryReportV2: (data) => apiRequest('DailyDeliveryReportV2', 'POST', data || {}, true),
  GetNextOrderNo: () => apiRequest('GetNextOrderNo', 'GET', null, true),
  SaveOrder: (data) => apiRequest('SaveOrder', 'POST', data, true),
  OrderByVoucherNo: (voucherNo) => apiRequest('OrderByVoucherNo', 'GET', voucherNo, true),
  CheckVoucherAssigned: (voucherNo) => apiRequest('CheckVoucherAssigned', 'GET', voucherNo, true),
  GetStoreRefNo: (locationId) => apiRequest('GetStoreRefNo', 'GET', locationId, true),
  GetCustomerAddress: (customerId) => apiRequest('GetCustomerAddress', 'GET', customerId, true),
  GetBackOrder: (orderId, type) => apiRequest('GetBackOrder', 'GET', `${orderId}/${type}`, true),
  DeleteOrder: (orderId) => apiRequest('DeleteOrder', 'GET', orderId, true),
  OrdersInHoldList: (data) => apiRequest('OrdersInHoldList', 'POST', data || {}, true),
  OrdersInHoldListList: (data) => apiRequest('OrdersInHoldListList', 'POST', data || {}, true),
  ReverseHoldOrders: (id) => apiRequest('ReverseHoldOrders', 'GET', id, true),
  CancelledZeroOrders: (data) => apiRequest('CancelledZeroOrders', 'POST', data || {}, true),
  CancelledZeroOrdersList: (data) => apiRequest('CancelledZeroOrdersList', 'POST', data || {}, true),
  ReverseCancelledOrders: (id) => apiRequest('ReverseCancelledOrders', 'GET', id, true),
  GetOrdersByBatchNo: (batchNo) => apiRequest('getOrdersByBatchNo', 'GET', batchNo, true),
  PrintByBatchNo: (batchNo) => apiRequest('PrintByBatchNo', 'GET', batchNo, true),
  TypesInDropDown: () => apiRequest('TypesInDropDown', 'GET', null, true),
  AgentsInDropDown: () => apiRequest('AgentsInDropDown', 'GET', null, true),

  // Followup (legacy Kendo)
  GetOrderInDFollowup: (data) => apiRequest('GetOrderInDFollowup', 'POST', data || {}, true),
  GetOrderInPFollowup: (data) => apiRequest('GetOrderInPFollowup', 'POST', data || {}, true),
  DriverFollowup: (data) => apiRequest('DriverFollowup', 'POST', data || {}, true),
  ClientPaymentClientFollowup: (data) => apiRequest('ClientPaymentClientFollowup', 'POST', data || {}, true),
  ClientReturnClientFollowup: (data) => apiRequest('ClientReturnClientFollowup', 'POST', data || {}, true),
  // Followup React lists — no Kendo. FollowupListQueryDto + Filters/Sort
  GetOrderInDFollowupList: (data) => apiRequest('GetOrderInDFollowupList', 'POST', data || {}, true),
  GetOrderInPFollowupList: (data) => apiRequest('GetOrderInPFollowupList', 'POST', data || {}, true),
  DriverFollowupList: (data) => apiRequest('DriverFollowupList', 'POST', data || {}, true),
  ClientPaymentClientFollowupList: (data) => apiRequest('ClientPaymentClientFollowupList', 'POST', data || {}, true),
  ClientReturnClientFollowupList: (data) => apiRequest('ClientReturnClientFollowupList', 'POST', data || {}, true),
  
  // Client Upload
  SaveClientList: (data) => apiRequest('SaveClientList', 'POST', data, true),

  // Approvals
  ShipmentsForApprovals: () => apiRequest('ShipmentsForApprovals', 'GET', null, true),
  ApproveShipment: (data) => apiRequest('ApproveShipment', 'POST', data, true),
  PrintShipment: (id) => apiRequest('PrintShipment', 'GET', id, true),
  SaveShipment: (data) => apiRequest('SaveShipment', 'POST', data, true),
  ShipmentById: (id) => apiRequest('ShipmentById', 'GET', id, true),
  UnApproveShipment: (id) => apiRequest(`UnApproveShipment/${id}`, 'POST', null, true),
  DeleteShipment: (id) => apiRequest('DeleteShipment', 'GET', id, true),
  GetNextShipmentNo: () => apiRequest('GetNextShipmentNo', 'GET', null, true),
  GetShipmentList: (data) => apiRequest('GetShipmentList', 'POST', data, true),
  GetShipmentListList: (data) => apiRequest('GetShipmentListList', 'POST', data || {}, true),
  ClientsInClientPaymentList: (data) => apiRequest('ClientsInClientPaymentList', 'POST', data, true),
  GetNextClientPaymentNo: () => apiRequest('GetNextClientPaymentNo', 'GET', null, true),
  SaveClientPayment: (data) => apiRequest('SaveClientPayment', 'POST', data, true),
  PrintClientPayment: (id) => apiRequest('PrintClientPayment', 'GET', id, true),
  ClientPaymentsList: (data) => apiRequest('ClientPaymentsList', 'POST', data, true),
  ClientPaymentsListList: (data) => apiRequest('ClientPaymentsListList', 'POST', data || {}, true),
  ClientPaymentsForApprovals: () => apiRequest('ClientPaymentsForApprovals', 'GET', null, true),
  ApproveClientPayment: (data) => apiRequest('ApproveClientPayment', 'POST', data, true),
  CPClientsInStatusList: (data) => apiRequest('CPClientsInStatusList', 'POST', data, true),
  getCPOrdersByCPCId: (id) => apiRequest('getCPOrdersByCPCId', 'GET', id, true),
  updateClientPaymentStatus: (data) => apiRequest('updateClientPaymentStatus', 'POST', data, true),
  ClientsInClientReturnList: (data) => apiRequest('ClientsInClientReturnList', 'POST', data, true),
  GetNextClientReturnNo: () => apiRequest('GetNextClientReturnNo', 'GET', null, true),
  SaveClientReturn: (data) => apiRequest('SaveClientReturn', 'POST', data, true),
  PrintClientReturn: (id) => apiRequest('PrintClientReturn', 'GET', id, true),
  PrintClientReturnList: (id) => apiRequest('PrintClientReturnList', 'GET', id, true),
  GetClientReturnById: (id) => apiRequest('GetClientReturnById', 'GET', id, true),
  ClientReturnsList: (data) => apiRequest('ClientReturnsList', 'POST', data, true),
  ClientReturnsListList: (data) => apiRequest('ClientReturnsListList', 'POST', data || {}, true),
  RemoveCRCOrder: (id) => apiRequest('RemoveCRCOrder', 'GET', id, true),
  RemoveCRCFromCR: (id) => apiRequest('RemoveCRCFromCR', 'GET', id, true),
  DeleteClientReturn: (id) => apiRequest('DeleteClientReturn', 'GET', id, true),
  UnapproveClientReturn: (id) => apiRequest('UnapproveClientReturn', 'GET', id, true),
  updateClientReturnStatus: (data) => apiRequest('updateClientReturnStatus', 'POST', data, true),
  getCROrdersByCRCId: (id) => apiRequest(`getCROrdersByCRCId/${id}`, 'GET', null, true),
  ClientReturnsForApprovals: (data) => apiRequest('ClientReturnsForApprovals', 'POST', data, true),
  ApproveClientReturn: (data) => apiRequest('ApproveClientReturn', 'POST', data, true),

  // Draft SMS
  ClientsInAutoComplete: (data) => apiRequest('ClientsInAutoComplete', 'POST', data, true),
  CustomersInAutoComplete: (data) => apiRequest('CustomersInAutoComplete', 'POST', data, true),
  AreasInGrid: (data) => apiRequest('AreasInGrid', 'POST', data || {}, true),
  DraftSMS: (data) => apiRequest('DraftSMS', 'POST', data, true),

  // Delivery Note SMS
  getDeliveryNoteMessageText: () => apiRequest('getDeliveryNoteMessageText', 'GET', null, true),
  GetDeliveryNoteById: (id) => apiRequest('GetDeliveryNoteById', 'GET', id, true),
  DeliveryNotesInGrid: (data) => apiRequest('DeliveryNotesInGrid', 'POST', data || {}, true),
  SendDirectDeliveryNoteSms: (data) => apiRequest('SendDirectDeliveryNoteSms', 'POST', data, true),

  // Store Management
  StoresInList: (data) => apiRequest('StoresInList', 'POST', data || {}, true),
  SaveStore: (data) => apiRequest('SaveStore', 'POST', data, true),
  DeleteStore: (id) => apiRequest('DeleteStore', 'GET', id, true),
  AddMultipleStores: (data) => apiRequest('AddMultipleStores', 'POST', data, true),
  MakePickupStatusInstore: (voucherno) => apiRequest(`MakePickupStatusInstore/${voucherno}`, 'GET', null, true),

  // Delivery Scan
  GetNextDeliveryNoteNo: () => apiRequest('GetNextDeliveryNoteNo', 'GET', null, true),
  GetConfirmedDeliveryNotes: () => apiRequest('GetConfirmedDeliveryNotes', 'GET', null, true),
  GetNewDeliveryNotes: () => apiRequest('GetNewDeliveryNotes', 'GET', null, true),
  GetDeliveryDetailsListById: (id) => apiRequest('GetDeliveryDetailsListById', 'GET', id, true),
  PrintDeliveryNoteAllocation: (id) => apiRequest('PrintDeliveryNoteAllocation', 'GET', id, true),
  PrintDeliveryNoteAllocation2: (id) => apiRequest('PrintDeliveryNoteAllocation2', 'GET', id, true),
  OrdersInDeliveryNote: (data) => apiRequest('OrdersInDeliveryNote', 'POST', data || {}, true),
  PushOrder: (data) => apiRequest('PushOrder', 'POST', data, true),
  TodaysDeliveryList: (skip = 0, take = 20) => apiRequest(`TodaysDeliveryList?skip=${skip}&take=${take}`, 'GET', null, true),
  TodaysDeliveryListList: (data) => apiRequest('TodaysDeliveryListList', 'POST', data || {}, true),
  TodaysDeliveryStatistics: () => apiRequest('TodaysDeliveryStatistics', 'GET', null, true),
  EmirateWiseDelivery: () => apiRequest('EmirateWiseDelivery', 'GET', null, true),
  AreaWiseOrders: () => apiRequest('AreaWiseOrders', 'GET', null, true),
  StatusWiseOrders: () => apiRequest('StatusWiseOrders', 'GET', null, true),
  AddScannedOrderToDN: (data) => apiRequest('AddScannedOrderToDN', 'POST', data, true),
  ConfirmDeliveryNote: (data) => apiRequest('ConfirmDeliveryNote', 'POST', data, true),
  DeliveryNotesInList: (data) => apiRequest('DeliveryNotesInList', 'POST', data, true),
  DeliveryNotesListList: (data) => apiRequest('DeliveryNotesListList', 'POST', data || {}, true),
  DeliveryStatistics: (data) => apiRequest('DeliveryStatistics', 'POST', data, true),
  CreateDeliveryNote: (data) => apiRequest('CreateDeliveryNote', 'POST', data, true),
  DeleteDeliveryNote: (id) => apiRequest('DeleteDeliveryNote', 'GET', id, true),
  removeDeliveryDetails: (data) => apiRequest('removeDeliveryDetails', 'POST', data, true),
  OrdersInDeliveryNoteV2: (data) => apiRequest('OrdersInDeliveryNoteV2', 'POST', data, true),
  assignOrdersToDN: (data) => apiRequest('assignOrdersToDN', 'POST', data, true),
  SaveStoreRefList: (data) => apiRequest('SaveStoreRefList', 'POST', data, true),
  ExchangeDeliveryNote: (data) => apiRequest('ExchangeDeliveryNote', 'POST', data, true),
  ChangeOrderDriverRemarks: (data) => apiRequest('ChangeOrderDriverRemarks', 'POST', data, true),

  // Pickup
  DriversInPickupAllocation: () => apiRequest('DriversInPickupAllocation', 'GET', null, true),
  PickupNotesInList: (data) => apiRequest('PickupNotesInList', 'POST', data, true),
  PickupNotesListList: (data) => apiRequest('PickupNotesListList', 'POST', data || {}, true),
  DeletePickupNote: (id) => apiRequest('DeletePickupNote', 'GET', id, true),
  GetPickupNoteById: (id) => apiRequest('GetPickupNoteById', 'GET', id, true),
  GetNextPickupNoteNo: () => apiRequest('GetNextPickupNoteNo', 'GET', null, true),
  CreatePickupNote: (data) => apiRequest('CreatePickupNote', 'POST', data, true),
  ConfirmPickupNote: (data) => apiRequest('ConfirmPickupNote', 'POST', data, true),
  OrdersInPickupNoteV2: (data) => apiRequest('OrdersInPickupNoteV2', 'POST', data, true),
  removePickupDetails: (data) => apiRequest('removePickupDetails', 'POST', data, true),
  assignOrdersToPN: (data) => apiRequest('assignOrdersToPN', 'POST', data, true),
  PrintPickUpAllocation: (id) => apiRequest('PrintPickUpAllocation', 'GET', id, true),
  ExchangePickupNote: (data) => apiRequest('ExchangePickupNote', 'POST', data, true),
  GetPickupDetailsListById: (id) => apiRequest('GetPickupDetailsListById', 'GET', id, true),
  PushPickupOrder: (data) => apiRequest('PushPickupOrder', 'POST', data, true),
  PickupStatistics: (data) => apiRequest('PickupStatistics', 'POST', data, true),
  StatusInPickupStatus: () => apiRequest('StatusInPickupStatus', 'GET', null, true),
  GetPickupNoteOnlyById: (id) => apiRequest('GetPickupNoteOnlyById', 'GET', id, true),
  GetPickupDetails: (data) => apiRequest('GetPickupDetails', 'POST', data, true),
  UpdateOrderPickUpStatus: (data) => apiRequest('UpdateOrderPickUpStatus', 'POST', data, true),
  SavePickupNoteStatus: (data) => apiRequest('SavePickupNoteStatus', 'POST', data, true),
  PrintPickUpStatus: (id) => apiRequest('PrintPickUpStatus', 'GET', id, true),

  // Counters
  CountersInList: (data) => apiRequest('CountersInList', 'POST', data || {}, true),
  SaveCounter: (data) => apiRequest('SaveCounter', 'POST', data, true),
  CloseOpenCounter: (id) => apiRequest('CloseOpenCounter', 'GET', id, true),
  UpdateLastNoCounter: (data) => apiRequest('updateLastNoCounter', 'POST', data, true),
  SaveAllCounter: (data) => apiRequest('SaveAllCounter', 'POST', data || [], true),
  DeleteCounter: (id) => apiRequest('DeleteCounter', 'GET', id, true),
  OpenAllCounter: () => apiRequest('OpenAllCounter', 'GET', null, true),
  CloseAllCounter: () => apiRequest('CloseAllCounter', 'GET', null, true),
  GetReturnedOrdersByAreaId: (data) => apiRequest('GetReturnedOrdersByAreaId', 'POST', data || {}, true),
  SaveStoreRefReturnOrders: (data) => apiRequest('saveStoreRefReturnOrders', 'POST', data || [], true),
  LockAreaRef: (id) => apiRequest('LockAreaRef', 'GET', id, true),
  OrdersInRefEdit: (data) => apiRequest('OrdersInRefEdit', 'POST', data || {}, true),

  GetNextShipmentNo: (data) => apiRequest('GetNextShipmentNo', 'GET', data, false),
  GetShipmentList: (data) => apiRequest('GetShipmentList', 'POST', data, true),

  // Activity tracker (authenticated)
  TrackActivity: (data) => apiRequest('TrackActivity', 'POST', data, true, false, true),
  ActivityTrackerInView: (data) => apiRequest('ActivityTrackerInView', 'POST', data || {}, true, false, true),
  ActivityTrackerFilterOptions: () => apiRequest('ActivityTrackerFilterOptions', 'GET', null, true, false, true),

  ClientPaymentsList: (data) => apiRequest('ClientPaymentsList', 'POST', data, true),

  GetAdminDashboardData: () => apiRequest('GetAdminDashboardData', 'GET', null, true),
  GetAdminDashboardDeliveryStatus: () => apiRequest('GetAdminDashboardDeliveryStatus', 'GET', null, true),
  TopClientsMonthly: () => apiRequest('TopClientsMonthly', 'GET', null, true),
  TopDeliveryAreas: () => apiRequest('TopDeliveryAreas', 'GET', null, true),
  DriversSummary: () => apiRequest('DriversSummary', 'GET', null, true),
  RecentOrders: (data) => apiRequest('RecentOrders', 'POST', data || {}, true),

};

export default MainService;
